/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useState, useEffect, use } from 'react';

const Theme = {
  DARK: 'dark',
  LIGHT: 'light',
} as const;

type Theme = (typeof Theme)[keyof typeof Theme];
const themes: Array<Theme> = Object.values(Theme);

type ThemeContextType = {
  theme: Theme;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextType>({
  theme: Theme.LIGHT,
  toggleTheme: () => {},
});

const prefersDarkMQ = '(prefers-color-scheme: dark)';
const getPreferredTheme = () =>
  window.matchMedia(prefersDarkMQ).matches ? Theme.DARK : Theme.LIGHT;

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  // Check local storage for saved theme preference, default to 'light' or system preference
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem('theme') as Theme | null;
    if (savedTheme && themes.includes(savedTheme)) {
      return savedTheme;
    }
    return getPreferredTheme();
  });

  useEffect(() => {
    // Apply the 'dark' class to the html element
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    // Persist theme choice in local storage
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  return <ThemeContext value={{ theme, toggleTheme }}>{children}</ThemeContext>;
};

export const useTheme = () => use(ThemeContext);
